import React, { useState } from 'react'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import {
    Col,
    Row,
    Tooltip,
} from 'reactstrap'

import {
    TIMED_MODE_PUMP_TIME_WORK,
    SENSOR_MODE_PUMP_TIME_WORK,
    OFF_PEAK_PUMP_TIME_WORK,
    HIGH_PEAK_PUMP_TIME_WORK,
    TIMED_MODE_PUMP_TIME_WAIT,
    OFF_PEAK_PUMP_TIME_WAIT,
    HIGH_PEAK_PUMP_TIME_WAIT,
    LOWER_DOSAGE_TRIGGER_ADDRESS,
    HIGHER_DOSAGE_TRIGGER_ADDRESS,
    PUMP_EVERY_N_PASSAGE,
    PUMP_PASSAGE_DELAY,
} from '../../../components/shared/device-addresses'
import DevicePumpSettingsInput from './device-pump-settings-input'
import {
    toggleTriggerDevice,
    DeviceEntity,
} from './helpers'
import Loader from '../../../components/shared/loader'
import { useAppSelector } from '../../../hooks/redux'

interface DeviceDataSetting {
    address: string
    hint: string
    data: string | number
}

interface DevicePumpTimingProps {
    device: DeviceEntity
    dataSettings?: DeviceDataSetting[]
    deviceHigherDosageActive?: string
    deviceLowerDosageActive?: string
    deviceIsTimeMode?: string
    className?: string
    deviceWriter?: string[]
}

/**
 * Pump timing settings for a single device.
 *
 * Displays work/wait timing inputs depending on the active mode
 * (time vs sensor) and exposes toggles for reduced/higher dosage.
 */
const DevicePumpTiming = ({
    device,
    dataSettings,
    deviceHigherDosageActive,
    deviceLowerDosageActive,
    deviceIsTimeMode,
}: DevicePumpTimingProps) => {
    const { t } = useTranslation()
    const deviceWriter = useAppSelector(state => state.devices.deviceWriter) as string[]
    const [isOpen, setIsOpen] = useState(false)

    const find = (hint: string): DeviceDataSetting | undefined =>
        dataSettings?.find?.(deviceData => deviceData?.hint === hint)

    const timedModePumpTimeWork = find(TIMED_MODE_PUMP_TIME_WORK)
    const sensorModePumpTimeWork = find(SENSOR_MODE_PUMP_TIME_WORK)
    const offPeakPumpTimeWork = find(OFF_PEAK_PUMP_TIME_WORK)
    const highPeakPumpTimeWork = find(HIGH_PEAK_PUMP_TIME_WORK)
    const timedModePumpTimeWait = find(TIMED_MODE_PUMP_TIME_WAIT)
    const offPeakPumpTimeWait = find(OFF_PEAK_PUMP_TIME_WAIT)
    const highPeakPumpTimeWait = find(HIGH_PEAK_PUMP_TIME_WAIT)
    const pumpEveryNPassage = find(PUMP_EVERY_N_PASSAGE)
    const pumpPassageDelay = find(PUMP_PASSAGE_DELAY)

    const isTimeMode = deviceIsTimeMode === '1'

    return (
        <Row>
            <Col lg={6}>
                <span className="my-3 d-block">
                    <i className="fas fa-play fa-lg mr-1" />
                    {t('deviceDetail.work')}
                </span>
                {isTimeMode ? (
                    <DevicePumpSettingsInput
                        title={t(`deviceData.hint.${timedModePumpTimeWork?.hint}`)}
                        data={timedModePumpTimeWork?.data}
                        measurement={t('seconds')}
                        device={device}
                        dataSetting={timedModePumpTimeWork}
                    />
                ) : (
                    <>
                        <DevicePumpSettingsInput
                            title={t(`deviceData.hint.${sensorModePumpTimeWork?.hint}`)}
                            data={sensorModePumpTimeWork?.data}
                            measurement={t('seconds')}
                            device={device}
                            dataSetting={sensorModePumpTimeWork}
                        />
                        <DevicePumpSettingsInput
                            title={t(`deviceData.hint.${pumpEveryNPassage?.hint}`)}
                            data={pumpEveryNPassage?.data}
                            device={device}
                            dataSetting={pumpEveryNPassage}
                        />
                    </>
                )}
                {isTimeMode && (
                    <>
                        <div className="d-flex align-items-center">
                            <div className="d-flex align-items-center mr-2">
                                <label className="switch mt-1">
                                    {deviceWriter.some(
                                        deviceWriterData =>
                                            deviceWriterData === LOWER_DOSAGE_TRIGGER_ADDRESS,
                                    ) ? (
                                            <Loader width="16" className="pl-1" />
                                        ) : (
                                            <>
                                                <input
                                                    type="checkbox"
                                                    checked={parseInt(deviceLowerDosageActive ?? '0', 10) !== 0}
                                                    onChange={() =>
                                                        toggleTriggerDevice(device, LOWER_DOSAGE_TRIGGER_ADDRESS)
                                                    }
                                                />
                                                <div className="slider" />
                                            </>
                                        )}
                                </label>
                            </div>
                            <span>
                                {t('deviceData.hint.ACTIVE_REDUCED_DOSAGE')}
                            </span>
                        </div>

                        <DevicePumpSettingsInput
                            title={t(`deviceData.hint.${offPeakPumpTimeWork?.hint}`)}
                            data={offPeakPumpTimeWork?.data}
                            measurement={t('seconds')}
                            device={device}
                            dataSetting={offPeakPumpTimeWork}
                        />
                    </>
                )}

                <div className="d-flex align-items-center">
                    <div className="d-flex align-items-center mr-2">
                        <label className="switch mt-1">
                            {deviceWriter.some(
                                deviceWriterData =>
                                    deviceWriterData === HIGHER_DOSAGE_TRIGGER_ADDRESS,
                            ) ? (
                                    <Loader width="16" className="pl-1" />
                                ) : (
                                    <>
                                        <input
                                            type="checkbox"
                                            checked={parseInt(deviceHigherDosageActive ?? '0', 10) !== 0}
                                            onChange={() => toggleTriggerDevice(device, HIGHER_DOSAGE_TRIGGER_ADDRESS)}
                                        />
                                        <div className="slider" />
                                    </>
                                )}
                        </label>
                    </div>
                    <span>{t('deviceData.hint.ACTIVE_HIGHER_DOSAGE')}</span>
                </div>
                <DevicePumpSettingsInput
                    title={t(`deviceData.hint.${highPeakPumpTimeWork?.hint}`)}
                    data={highPeakPumpTimeWork?.data}
                    measurement={t('seconds')}
                    device={device}
                    dataSetting={highPeakPumpTimeWork}
                />
            </Col>
            <Col lg={6}>
                <span className="my-3 d-block">
                    <i className="fas fa-pause fa-lg mr-1" />
                    {t('deviceDetail.pause')}
                </span>
                {isTimeMode && (
                    <DevicePumpSettingsInput
                        title={t(`deviceData.hint.${timedModePumpTimeWait?.hint}`)}
                        data={timedModePumpTimeWait?.data}
                        measurement={t('seconds')}
                        device={device}
                        dataSetting={timedModePumpTimeWait}
                    />
                )}
                {!isTimeMode && (
                    <DevicePumpSettingsInput
                        title={t('deviceData.hint.PUMP_PASSAGE_DELAY_HINT')}
                        data={Number(pumpPassageDelay?.data ?? 0) / 100}
                        measurement={t('seconds')}
                        device={device}
                        dataSetting={pumpPassageDelay}
                        className="row-align-passage-input"
                    >
                        <i className="fas fa-info-circle" id="pop3" />
                        <Tooltip
                            placement="top"
                            isOpen={isOpen}
                            target="pop3"
                            toggle={() => setIsOpen(previous => !previous)}
                        >
                            {t('deviceData.hint.PUMP_PASSAGE_DELAY_ABBR')}
                        </Tooltip>
                    </DevicePumpSettingsInput>
                )}
                {isTimeMode && (
                    <DevicePumpSettingsInput
                        title={t(`deviceData.hint.${offPeakPumpTimeWait?.hint}`)}
                        data={offPeakPumpTimeWait?.data}
                        measurement={t('seconds')}
                        device={device}
                        dataSetting={offPeakPumpTimeWait}
                        className="row-align"
                    />
                )}
                <DevicePumpSettingsInput
                    title={t(`deviceData.hint.${highPeakPumpTimeWait?.hint}`)}
                    data={highPeakPumpTimeWait?.data}
                    measurement={t('seconds')}
                    device={device}
                    dataSetting={highPeakPumpTimeWait}
                    className={classNames({
                        'row-align': isTimeMode,
                        'row-align-sensor-mode': !isTimeMode,
                    })}
                />
            </Col>
        </Row>
    )
}

export default DevicePumpTiming
